import { EventType, type eventWithTime } from '@rrweb/types'
import { Replayer, type ReplayPlugin } from 'rrweb'
import { RRWEB_EVENTS } from '@simplisafe/compass-web'
import { getVideoIdentifier } from '../rrwebUtils'
import type {
  ProcessedRRWebEvents,
  VideoEndEvent,
  VideoStartEvent,
  VideoStateChangeEvent,
  VideoMirrorStartEvent,
  VideoMirrorEndEvent
} from '../types'
import { Camera } from '../../../../../api/models'
import { CLEAR_VIDEO_PLACEHOLDERS_EVENT } from '../../ErtPreprocessor'
import { ErtPlugin } from './ErtPlugin'
import { LoggerName, namedLogger } from '../../../../../utils/logger'

const CONSOLE_PLAYBACK_PREFIX = 'CONSOLE_PLAYBACK:'
let instanceNumber = 0

const logger = namedLogger(LoggerName.RRWebPlugin)

export class RRWebPlugin implements ReplayPlugin {
  private readonly processedEvents: ProcessedRRWebEvents
  private readonly camerasById: Map<string, Camera>

  private ertPlugin: ErtPlugin | undefined
  private readonly ertPluginPromise: Promise<ErtPlugin>
  private ertPluginResolve: ((plugin: ErtPlugin) => void) | undefined

  constructor(processedEvents: ProcessedRRWebEvents, camerasById: Map<string, Camera>) {
    this.processedEvents = processedEvents
    this.camerasById = camerasById
    this.ertPluginPromise = new Promise<ErtPlugin>(resolve => {
      this.ertPluginResolve = resolve
    })
    instanceNumber++
  }

  /**
   * this allows us to know when a new plugin is instantiated and remount the player UI if needed
   */
  public getInstanceNumber(): number {
    return instanceNumber
  }

  public async waitForInitializationToComplete(): Promise<RRWebPlugin> {
    if (this.ertPlugin) {
      return this
    } else {
      await this.ertPluginPromise
      return this
    }
  }

  public getErtPlugin(): ErtPlugin {
    if (!this.ertPlugin) {
      throw new Error('ErtPlugin has not been initialized yet')
    }
    return this.ertPlugin
  }

  public getProcessedEvents(): ProcessedRRWebEvents {
    return this.processedEvents
  }

  /**
   * This implements the RRWeb Plugin Interface and is called every time an event is processed by the player
   *
   * One thing to note. RRWeb does not process custom events when seeking, so isSync will be true when the event is processed by a seek event.
   *
   * @param event
   * @param isSync - isSync will be true when the event is processed by a seek event
   * @param context
   */
  public async handler(event: eventWithTime, isSync: boolean, context: { replayer: Replayer }) {
    if (!this.ertPlugin) {
      throw new Error('ErtPlugin has not been initialized yet')
    }

    try {
      // keep the replayer reference up to date in the ertPlugin
      this.ertPlugin.setReplayer(context.replayer)

      if (event.type === EventType.Plugin) {
        this.processConsoleEvent(event)
      }

      if (event.type === EventType.FullSnapshot) {
        logger.debug('FullSnapshot isSync', isSync, {
          console: {
            event
          }
        })
        this.ertPlugin.processFullSnapshotEvent(event, isSync)
      }

      if (event.type === EventType.Custom) {
        if (event.data.tag === RRWEB_EVENTS.VIDEO_START) {
          logger.debug(event.data.tag, ' isSync', isSync, getVideoIdentifier(event.data.payload as VideoStartEvent), {
            console: {
              payload: event.data.payload
            }
          })
          await this.ertPlugin.processVideoStartEvent(event.data.payload as VideoStartEvent)
        } else if (event.data.tag === RRWEB_EVENTS.VIDEO_END) {
          logger.debug(event.data.tag, ' isSync', isSync, getVideoIdentifier(event.data.payload as VideoEndEvent), {
            console: {
              payload: event.data.payload
            }
          })
          this.ertPlugin.processVideoEndEvent(event.data.payload as VideoEndEvent)
        } else if (event.data.tag === RRWEB_EVENTS.VIDEO_STATE_CHANGE) {
          const videoStateChangeEvent = event.data.payload as VideoStateChangeEvent
          logger.debug(event.data.tag, ' isSync', isSync, getVideoIdentifier(videoStateChangeEvent), videoStateChangeEvent.trigger, {
            console: {
              payload: event.data.payload
            }
          })
          this.ertPlugin.processVideoStateChangeEvent(videoStateChangeEvent)
        } else if (event.data.tag === RRWEB_EVENTS.VIDEO_MIRROR_START) {
          const videoMirrorStartEvent = event.data.payload as VideoMirrorStartEvent
          logger.debug(event.data.tag, ' isSync', isSync, getVideoIdentifier(videoMirrorStartEvent), {
            console: {
              payload: event.data.payload
            }
          })
          this.ertPlugin.processVideoMirrorStartEvent(videoMirrorStartEvent)
        } else if (event.data.tag === RRWEB_EVENTS.VIDEO_MIRROR_END) {
          const videoMirrorEndEvent = event.data.payload as VideoMirrorEndEvent
          logger.debug(event.data.tag, ' isSync', isSync, getVideoIdentifier(videoMirrorEndEvent), {
            console: {
              payload: event.data.payload
            }
          })
          this.ertPlugin.processVideoMirrorEndEvent(videoMirrorEndEvent)
        } else if (event.data.tag === CLEAR_VIDEO_PLACEHOLDERS_EVENT) {
          logger.debug(event.data.tag, ' isSync', isSync, {
            console: {
              event
            }
          })
          this.ertPlugin.processClearVideoPlaceholdersEvent()
        } else {
          logger.debug(event.data.tag, ' isSync', isSync, {
            console: {
              payload: event.data.payload
            }
          })
        }
      }
    } catch (error) {
      logger.error('Error processing event in RRWebPlugin handler', error, event)
    }
  }

  /**
   * This implements the RRWeb Plugin interface and is called when it adds a node to the player's DOM.
   * @param node
   * @param context
   */
  public onBuild(
    node: Node | unknown,
    context: {
      id: number
      replayer: Replayer
    }
  ) {
    try {
      const el = node as HTMLElement

      // we can't initialize the plugin until we have access to the document. This is often not the very first time onBuild is called, so we check here and initialize if needed.
      if (!this.ertPlugin && el.ownerDocument?.body !== null) {
        this.ertPlugin = new ErtPlugin(this.processedEvents, this.camerasById, el.ownerDocument, context.replayer)
        if (this.ertPluginResolve) {
          this.ertPluginResolve(this.ertPlugin)
        }
      }

      // keep the replayer reference up to date in the ertPlugin
      if (this.ertPlugin) {
        this.ertPlugin.setReplayer(context.replayer)

        this.ertPlugin.elementBuilt(el)
      }
    } catch (error) {
      logger.error('Error in RRWebPlugin onBuild', error, node, context)
    }
  }

  /**
   * This implements the RRWeb Plugin interface, but I'm not sure how it is useful
   * @param mirrors
   */
  public getMirror(mirrors: { nodeMirror: unknown }) {}

  private processConsoleEvent = (consoleEvent: eventWithTime) => {
    // @ts-expect-error ignoring since this event is the exact same type as it is in the function that provides this.
    const consoleEventPayload = consoleEvent.data.payload
    const consoleEventType = consoleEventPayload.level

    if (consoleEventType === 'log') {
      console.log.apply(window, [CONSOLE_PLAYBACK_PREFIX, ...consoleEventPayload.payload])
    } else if (consoleEventType === 'trace') {
      console.log.apply(window, [CONSOLE_PLAYBACK_PREFIX, ...consoleEventPayload.payload, ...consoleEventPayload.trace])
    } else if (consoleEventType === 'warn') {
      console.warn.apply(window, [CONSOLE_PLAYBACK_PREFIX, ...consoleEventPayload.payload])
    } else if (consoleEventType === 'error') {
      console.error.apply(window, [CONSOLE_PLAYBACK_PREFIX, ...consoleEventPayload.payload])
    }
  }
}
